import { setActiveTeam } from '@/lib/api';
import { postAuthEvent } from '@/lib/auth-channel';
import { rememberTeamChoice } from '@/lib/tab-team';
import { useAuthStore, type AuthResponse } from '@/stores/authStore';
import type { Team } from '@/types/User';
import { beginTeamExit } from '@/utils/team-exit';
import { clearTeamSettingsCache } from '@/utils/teamSettingsCache';
import { discardUnsavedChanges } from '@/utils/unsavedChanges';

/**
 * An explicit team choice in this tab: new tabs open on it, and if this tab moved team the other
 * tabs are told (they offer to follow). Only explicit choices announce, never a silent refresh.
 */
function noteTeamChoice(
  previousTeamId: string | undefined,
  userId: string | undefined,
  team: { id: string; displayname: string } | undefined,
): void {
  if (!team?.id) return;
  rememberTeamChoice(team.id);
  if (userId && team.id !== previousTeamId) {
    postAuthEvent({ type: 'team-switched', userId, teamId: team.id, teamName: team.displayname });
  }
}

/** Stores a session the person chose (a team switch, a linked store) in this tab only. */
export function applyTeamSession(data: AuthResponse): void {
  const store = useAuthStore.getState();
  const previousTeamId = store.user?.active_team?.id;
  clearTeamSettingsCache();
  store.updateUserAndToken(data);
  noteTeamChoice(previousTeamId, data.user?.id, data.user?.active_team);
}

/** After accepting an invite: this tab moves to the team just joined. */
export function applyJoinedTeam(team: Team, token: string, role?: string): void {
  const store = useAuthStore.getState();
  const previousTeamId = store.user?.active_team?.id;
  store.updateUserWithTeam(team, token, role);
  noteTeamChoice(previousTeamId, store.user?.id, team);
}

/**
 * Switch the active company/store, then reload so no team-scoped page data can go stale. Callers
 * ask about unsaved work first (confirmDiscardUnsavedChanges). A failed switch rejects with
 * nothing changed and the page still open; a successful one ends by starting the reload.
 */
export async function switchActiveTeam(teamId: string, options?: { to?: string }): Promise<void> {
  const response = await setActiveTeam(teamId);

  if (response.error || !response.data?.user?.active_team) {
    throw new Error(response.error || 'Failed to switch team');
  }

  const activeTeam = response.data.user.active_team;

  // Only now the switch has gone through: throw away the drafts the person agreed to lose, and
  // unmount the team pages while this tab still holds the old team's token.
  discardUnsavedChanges();
  await beginTeamExit(`Switching to ${activeTeam.displayname}…`);

  // Persist writes are synchronous, so the new session is stored before the reload.
  applyTeamSession(response.data);
  window.location.href = options?.to ?? '/';
}
